import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DocumentApi } from '../../services/api/DocumentApi';
import { EndeavorApi } from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { getAudioDocumentTypeIds } from '../../utils/documentTypeUtils';
import './SidebarDocumentsList.css';

const FALLBACK_ROW_HEIGHT = 52;
const MIN_PAGE_SIZE = 8;
const MAX_PAGE_SIZE = 40;

const getDocId = (d) => String(
  d?.documentId ??
  d?.documentID ??
  d?.document_id ??
  d?.documentid ??
  d?.docId ??
  d?.docID ??
  d?.document?.documentId ??
  d?.document?.documentID ??
  d?.document?.document_id ??
  d?.document?.documentid ??
  d?.document?.id ??
  d?.id ??
  ''
);
const getDocName = (d) => String(d?.name ?? d?.title ?? d?.filename ?? d?.fileName ?? 'Untitled');
const getDocTypeName = (d) => String(d?.documentTypeName ?? d?.documentType ?? d?.typeName ?? '');

function formatRelative(isoLike) {
  if (!isoLike) return '';
  const t = new Date(isoLike).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(diff / 86400000);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  // fall back to month/day
  try {
    return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default function SidebarDocumentsList({
  selectedDocumentId,
  onSelectDocument,
  onTotalChange,
  onItemsChange,
  onUploadClick,
  pageSize: pageSizeProp,
  showType = true,
  showRelativeDate = true,
}) {
  const { user } = useAuth();
  const { documentTypes } = useData();

  const userId = user?.id || user?.userId;

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [dynamicPageSize, setDynamicPageSize] = useState(null);
  
  // Use ref to track loading state for concurrent request prevention
  const loadingRef = useRef(false);
  const bodyRef = useRef(null);

  // Dynamically compute page size from the list body height
  const computePageSize = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const h = el.getBoundingClientRect().height;
    if (h <= 0) return;
    const firstRow = el.querySelector('.sdlist-item');
    const rowH = firstRow ? firstRow.getBoundingClientRect().height : FALLBACK_ROW_HEIGHT;
    if (rowH <= 0) return;
    const fits = Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, Math.floor(h / rowH)));
    setDynamicPageSize(prev => prev !== fits ? fits : prev);
  }, []);

  // Measure on container resize
  useEffect(() => {
    if (pageSizeProp) return;
    const el = bodyRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => computePageSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [pageSizeProp, computePageSize]);

  // Re-measure after items render (actual row height may differ from fallback)
  useEffect(() => {
    if (pageSizeProp || items.length === 0) return;
    // Wait one frame so rows are painted and measurable
    const raf = requestAnimationFrame(() => computePageSize());
    return () => cancelAnimationFrame(raf);
  }, [pageSizeProp, items, computePageSize]);

  const pageSize = pageSizeProp || dynamicPageSize || 0;

  // Filter states
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [courseFilter, setCourseFilter] = useState('all');
  const [endeavorMap, setEndeavorMap] = useState({});

  // debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // reset paging when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, refreshNonce, pageSize, sortField, sortDirection, courseFilter]);

  // Load endeavors for course filter
  useEffect(() => {
    if (!userId) return;
    const loadEndeavors = async () => {
      try {
        const resp = await EndeavorApi.getEndeavors(userId, { page: 0, size: 1000 });
        if (resp?.ok) {
          const data = await resp.json();
          const list = Array.isArray(data) ? data : (data.content || data.endeavors || []);
          const map = {};
          list.forEach(e => {
            const id = e.id || e.endeavorId;
            const name = e.name || e.title || `Course ${id}`;
            if (id) map[id] = name;
          });
          setEndeavorMap(map);
        }
      } catch (e) {
        console.error('Failed to load endeavors:', e);
      }
    };
    loadEndeavors();
  }, [userId]);

  // Debug pagination state
  useEffect(() => {
    const nextDisabled = loading || totalPages === 0 || page >= totalPages - 1;
    console.log('[SidebarDocumentsList] Pagination state:', {
      page,
      totalPages,
      totalElements,
      loading,
      nextDisabled,
      calculation: `${page} >= ${totalPages - 1}`
    });
  }, [page, totalPages, totalElements, loading]);

  const loadPage = useCallback(async () => {
    if (!userId) return;
    if (!pageSize) return; // Wait until dynamic page size is measured
    if (loadingRef.current) return; // Prevent concurrent requests

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const audioTypeIds = await getAudioDocumentTypeIds(documentTypes);
      const excludeTypes = [...audioTypeIds, 6]; // notes

      // Map UI sort field to backend sort
      const backendSort = (() => {
        switch (sortField) {
          case 'name':
            return [`name,${sortDirection}`];
          case 'date':
            return [`createdAt,${sortDirection}`];
          case 'type':
            return [`documentTypeId,${sortDirection}`, 'name,asc'];
          case 'course':
            return [`createdAt,${sortDirection}`]; // fallback, client-side sort below
          default:
            return ['createdAt,desc'];
        }
      })();

      const params = {
        page,
        size: pageSize,
        sort: backendSort,
        excludeDocumentTypeIds: excludeTypes,
        _ts: Date.now(),
      };
      if (debouncedQuery.length > 1) params.name = debouncedQuery;
      if (courseFilter !== 'all') params.endeavorId = Number(courseFilter);

      const resp = await DocumentApi.getDocumentsByUser(userId, params);
      if (!resp?.ok) throw new Error(`Failed (${resp?.status || 'unknown'})`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (data.content || data.documents || []);

      console.log('[SidebarDocumentsList] Pagination data:', {
        totalPages: data.totalPages,
        totalElements: data.totalElements,
        currentPage: page,
        listLength: list.length
      });

      setItems(list);
      if (onItemsChange) onItemsChange(list);
      setTotalPages(data.totalPages ?? 1);
      const total = data.totalElements ?? list.length;
      setTotalElements(total);
      if (onTotalChange) onTotalChange(total);
    } catch (e) {
      setError(e);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page, pageSize, debouncedQuery, sortField, sortDirection, courseFilter, documentTypes, onTotalChange]);

  // initial + page changes
  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // react to uploads etc
  useEffect(() => {
    const handler = () => {
      setRefreshNonce(n => n + 1);
    };
    window.addEventListener('documents:changed', handler);
    return () => window.removeEventListener('documents:changed', handler);
  }, []);

  const rows = useMemo(() => {
    return items.map((d) => {
      const id = getDocId(d);
      const selected = selectedDocumentId != null && String(selectedDocumentId) === id;
      const name = getDocName(d);
      const typeName = getDocTypeName(d);
      const createdAt = d?.createdAt || d?.uploadedAt || d?.created_at || d?.upload_date || d?.date_uploaded || d?.timestamp;
      const rel = showRelativeDate ? formatRelative(createdAt) : '';
      const metaBits = [];
      if (showType && typeName) metaBits.push(typeName);
      if (rel) metaBits.push(rel);
      const meta = metaBits.join(' • ');

      return { id, name, meta, typeName, rel, selected, raw: d };
    });
  }, [items, selectedDocumentId, showType, showRelativeDate]);

  const resolveAndSelect = useCallback(async (row) => {
    if (!row) return;
    // If the list payload includes an id, use it.
    if (row.id) {
      onSelectDocument && onSelectDocument(row.id);
      return;
    }
    // Fallback: resolve ID by querying the backend by exact-ish name.
    // Some API payloads omit ids; this keeps deep-link (?doc=) working.
    try {
      if (!userId) return;
      const resp = await DocumentApi.getDocumentsByUser(userId, {
        page: 0,
        size: 10,
        sort: 'createdAt,desc',
        name: row.name,
        _ts: Date.now(),
      });
      if (!resp?.ok) return;
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (data.content || data.documents || []);
      const match = list.find(d => getDocName(d) === row.name) || list[0];
      let resolvedId = match ? getDocId(match) : '';

      // Secondary fallback: global search endpoint sometimes returns a different shape
      if (!resolvedId) {
        const resp2 = await DocumentApi.searchDocumentsByName(row.name);
        if (resp2?.ok) {
          const data2 = await resp2.json();
          const list2 = Array.isArray(data2) ? data2 : (data2.content || data2.documents || data2.data || []);
          const match2 = list2.find(d => getDocName(d) === row.name) || list2[0];
          resolvedId = match2 ? getDocId(match2) : '';
        }
      }

      if (resolvedId) onSelectDocument && onSelectDocument(resolvedId);
    } catch {
      // ignore
    }
  }, [onSelectDocument, userId]);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  // Kebab menu state
  const [menuOpenId, setMenuOpenId] = useState(null);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpenId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const resp = await DocumentApi.deleteDocument(deleteTarget.id);
      if (resp && !resp.ok && resp.status !== 204) {
        throw new Error(`Delete failed (${resp.status})`);
      }
      // If the deleted document is currently selected, deselect it
      if (String(selectedDocumentId) === String(deleteTarget.id)) {
        onSelectDocument && onSelectDocument(null);
      }
      // Refresh the list
      setRefreshNonce(n => n + 1);
      window.dispatchEvent(new CustomEvent('documents:changed'));
    } catch (e) {
      console.error('[SidebarDocumentsList] Delete failed:', e);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }, [deleteTarget, selectedDocumentId, onSelectDocument]);

  const hasActiveFilters = debouncedQuery || courseFilter !== 'all';

  const clearAllFilters = () => {
    setQuery('');
    setCourseFilter('all');
  };

  return (
    <div className="sdlist" data-testid="sidebar-documents-list">
      <div className="sdlist-search" role="search">
        <input
          className="sdlist-searchInput"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search documents…"
          aria-label="Search documents"
        />
      </div>

      {/* Filter Controls */}
      <div className="sdlist-filters">
        <div className="filter-row-dual">
          <select
            className="filter-select"
            value={`${sortField}-${sortDirection}`}
            onChange={(e) => {
              const [field, dir] = e.target.value.split('-');
              setSortField(field);
              setSortDirection(dir);
            }}
            aria-label="Sort documents"
          >
            <option value="date-desc">📅 Newest First</option>
            <option value="date-asc">📅 Oldest First</option>
            <option value="name-asc">🔤 Name A-Z</option>
            <option value="name-desc">🔤 Name Z-A</option>
          </select>

          <select
            className="filter-select"
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
            aria-label="Filter by course"
          >
            <option value="all">All Courses</option>
            {Object.entries(endeavorMap)
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
          </select>
        </div>
      </div>

      {/* Filter Chips */}
      {hasActiveFilters && (
        <div className="sdlist-filter-chips">
          {debouncedQuery && (
            <div className="filter-chip">
              <span className="chip-label">"{debouncedQuery}"</span>
              <button
                className="chip-remove"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            </div>
          )}
          {courseFilter !== 'all' && (
            <div className="filter-chip">
              <span className="chip-label">{endeavorMap[courseFilter]}</span>
              <button
                className="chip-remove"
                onClick={() => setCourseFilter('all')}
                aria-label="Clear course filter"
              >
                ×
              </button>
            </div>
          )}
          <button
            className="clear-all-btn"
            onClick={clearAllFilters}
            aria-label="Clear all filters"
          >
            Clear All
          </button>
        </div>
      )}

      <ul className="sdlist-body" ref={bodyRef} aria-label="Documents">
        {rows.map(r => (
          <li key={r.id || r.name} className="sdlist-item">
            <button
              type="button"
              className={`sdlist-row ${r.selected ? 'is-selected' : ''}`}
              onClick={() => resolveAndSelect(r)}
              aria-current={r.selected ? 'true' : undefined}
            >
              <div className="sdlist-fileIcon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="sdlist-textContainer">
                <div className="sdlist-name" title={r.name}>{r.name}</div>
                <div className="sdlist-metaRow">
                  {r.typeName && <span className="sdlist-metaCourse">{r.typeName}</span>}
                  {r.rel && <span className="sdlist-metaDate">{r.rel}</span>}
                </div>
              </div>
            </button>
            <div className="sdlist-menuWrapper" ref={menuOpenId === r.id ? menuRef : null}>
              <button
                type="button"
                className="sdlist-menuBtn"
                title="More actions"
                aria-label={`Actions for ${r.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpenId(menuOpenId === r.id ? null : r.id);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>
              {menuOpenId === r.id && (
                <div className="sdlist-menu">
                  <button
                    type="button"
                    className="sdlist-menuItem"
                    onClick={(e) => {
                      e.stopPropagation();
                      resolveAndSelect(r);
                      setMenuOpenId(null);
                    }}
                  >
                    Open
                  </button>
                  <button
                    type="button"
                    className="sdlist-menuItem sdlist-menuItem--danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({ id: r.id, name: r.name });
                      setMenuOpenId(null);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}

        {!loading && !error && rows.length === 0 && hasActiveFilters && (
          <li className="sdlist-empty">No matches found</li>
        )}

        {!loading && !error && rows.length === 0 && !hasActiveFilters && (
          <li className="documents-empty-state" aria-label="No documents">
            <div className="documents-empty-state__inner">
              <img
                src="/scout_images/scout_thinking.png"
                alt="Scout thinking"
                className="documents-empty-state__image"
              />
              <h3 className="documents-empty-state__title">No documents yet</h3>
              <p className="documents-empty-state__text">
                Upload your first file to start chatting, generating notes, and using study tools.
              </p>
              <button
                type="button"
                className="documents-empty-state__button"
                onClick={onUploadClick}
              >
                Upload documents
              </button>
              <div className="documents-empty-state__meta">
                PDF &bull; DOCX &bull; PPTX &bull; TXT &bull; Markdown
              </div>
            </div>
          </li>
        )}

        {error && (
          <li className="sdlist-error" role="status">
            Couldn't load documents.
          </li>
        )}

        {loading && (
          <li className="sdlist-loading">
            <div className="loading-spinner" />
            <span>Loading...</span>
          </li>
        )}
      </ul>

      {/* Pagination Controls */}
      <div className="sdlist-footer">
        <div className="pagination-controls">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => {
              console.log('[SidebarDocumentsList] Prev clicked, current page:', page);
              setPage(p => p - 1);
            }}
            disabled={loading || page === 0}
            aria-label="Previous page"
          >
            ‹ Prev
          </button>
          <span className="pagination-info">
            Page {page + 1} of {totalPages || 1}
          </span>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => {
              console.log('[SidebarDocumentsList] Next clicked, current page:', page, 'totalPages:', totalPages);
              setPage(p => p + 1);
            }}
            disabled={loading || totalPages === 0 || page >= totalPages - 1}
            aria-label="Next page"
            data-testid="pagination-next"
          >
            Next ›
          </button>
        </div>
        <div className="pagination-total">
          {totalElements} document{totalElements !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Delete confirmation dialog — rendered via portal so it isn't clipped */}
      {deleteTarget && createPortal(
        <div className="sdlist-delete-overlay" role="dialog" aria-modal="true" aria-label="Confirm delete" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="sdlist-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="sdlist-delete-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h3 className="sdlist-delete-title">Delete Document</h3>
            <p className="sdlist-delete-body">
              Are you sure you want to delete <strong>&ldquo;{deleteTarget.name}&rdquo;</strong>? This action cannot be undone.
            </p>
            <div className="sdlist-delete-actions">
              <button
                type="button"
                className="sdlist-delete-cancelBtn"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sdlist-delete-confirmBtn"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
